import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

/**
 * SessionAccordion - An expandable session list themed to event brand
 *
 * Features:
 * - Collapsed by default showing session count and preview
 * - Animated chevron rotation
 * - Smooth height transition
 * - Staggered session reveal animation
 * - Dynamic theming based on event brand color
 */

// Convert hex to RGB values
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 28, g: 25, b: 23 }; // stone-900 fallback
};

// Determine if color is light or dark for contrast
const isLightColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

export default function SessionAccordion({
  period,
  sessions,
  blockedContent,
  renderSession,
  formatPeriodTime,
  defaultExpanded = false,
  brandColor = '#1c1917', // Default to stone-900
  hasRegisteredSession = false, // Whether user has a session registered for this period
  registeredSessionTitle = '', // Title of the registered session
  isPresenterSession = false, // Whether the registered session is one the user is presenting
  isNotAttending = false, // Whether user marked this period as not attending
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);

  // Compute brand-derived colors
  const colors = useMemo(() => {
    const rgb = hexToRgb(brandColor);
    const isLight = isLightColor(brandColor);

    return {
      brand: brandColor,
      brandRgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`,
      // Text color for elements on brand background
      onBrand: isLight ? '#1c1917' : '#ffffff',
      // Subtle tint for backgrounds
      tint: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06)`,
      tintMedium: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
      // Border accent
      border: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`,
    };
  }, [brandColor]);

  // Measure content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [sessions, blockedContent, isExpanded]);

  const sessionCount = sessions?.length || 0;
  const firstSession = sessions?.[0];
  const hasContent = sessionCount > 0 || blockedContent;

  if (!hasContent) return null;

  return (
    <div className="relative group/accordion">
      {/* Accordion Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-4 p-4 rounded-xl border transition-all duration-300 ease-out"
        style={{
          backgroundColor: isExpanded ? colors.tint : '#fafaf9',
          borderColor: isExpanded ? colors.border : '#e7e5e4',
          borderLeftWidth: isExpanded ? '4px' : '1px',
          borderLeftColor: isExpanded ? brandColor : '#e7e5e4',
        }}
        aria-expanded={isExpanded}
        onMouseEnter={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.boxShadow = `0 4px 12px rgba(${colors.brandRgb}, 0.08)`;
          }
        }}
        onMouseLeave={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.borderColor = '#e7e5e4';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        {/* Left side: Period info */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Period badge - uses brand color */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-300"
            style={{
              backgroundColor: isExpanded ? brandColor : colors.tintMedium,
              color: isExpanded ? colors.onBrand : '#44403c',
            }}
          >
            <span>{period.name}</span>
          </div>

          {/* Time range */}
          <span className="text-sm text-stone-500">
            {formatPeriodTime(period)}
          </span>

          {/* Preview text when collapsed - show registered session or not-attending status */}
          {!isExpanded && (
            isNotAttending ? (
              <span className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-stone-400">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Not attending
              </span>
            ) : hasRegisteredSession ? (
              <span className={`hidden sm:flex items-center gap-1.5 text-sm font-medium truncate max-w-[300px] ${isPresenterSession ? 'text-emerald-600' : 'text-green-600'}`}>
                {isPresenterSession ? (
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="truncate">{registeredSessionTitle}</span>
                {isPresenterSession && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-emerald-600 text-white font-semibold flex-shrink-0">
                    Presenter
                  </span>
                )}
              </span>
            ) : firstSession && (
              <span className="hidden sm:block text-sm text-stone-400 truncate max-w-[200px]">
                — {firstSession.title}
                {sessionCount > 1 && ` +${sessionCount - 1} more`}
              </span>
            )
          )}
        </div>

        {/* Right side: Registered indicator + Count badge + Chevron */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Status indicator for mobile (shows on small screens) */}
          {isNotAttending && !isExpanded && (
            <div className="sm:hidden w-6 h-6 rounded-full flex items-center justify-center bg-stone-100">
              <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
          )}
          {hasRegisteredSession && !isNotAttending && !isExpanded && (
            <div className={`sm:hidden w-6 h-6 rounded-full flex items-center justify-center ${isPresenterSession ? 'bg-emerald-100' : 'bg-green-100'}`}>
              {isPresenterSession ? (
                <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          )}

          {/* Session count badge */}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-all duration-300"
            style={{
              backgroundColor: isExpanded ? `rgba(${colors.brandRgb}, 0.15)` : colors.tint,
              color: isExpanded ? brandColor : '#57534e',
            }}
          >
            <svg
              className="w-3.5 h-3.5"
              style={{ color: isExpanded ? brandColor : '#78716c' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>{sessionCount}</span>
          </div>

          {/* Animated chevron */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ease-out"
            style={{
              backgroundColor: isExpanded ? `rgba(${colors.brandRgb}, 0.12)` : '#f5f5f4',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <svg
              className="w-5 h-5"
              style={{ color: isExpanded ? brandColor : '#78716c' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Expandable Content Area */}
      <div
        className="overflow-hidden transition-all duration-500 ease-out"
        style={{
          maxHeight: isExpanded ? `${contentHeight + 32}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div ref={contentRef} className="pt-4 space-y-3">
          {/* Blocked/continuing session */}
          {blockedContent}

          {/* Session cards with staggered animation */}
          {sessions?.map((session, index) => (
            <div
              key={session.id}
              className="transform transition-all duration-300 ease-out"
              style={{
                transitionDelay: isExpanded ? `${index * 50}ms` : '0ms',
                opacity: isExpanded ? 1 : 0,
                transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
              }}
            >
              {renderSession(session, () => setIsExpanded(false))}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom accent line when collapsed (indicates expandability) */}
      {!isExpanded && sessionCount > 0 && (
        <div
          className="absolute -bottom-0.5 left-6 right-6 h-0.5 rounded-full opacity-30 transition-opacity group-hover/accordion:opacity-60"
          style={{ backgroundColor: brandColor }}
        />
      )}
    </div>
  );
}

/**
 * SessionAccordionGroup - Wrapper to manage multiple accordions
 * Optionally allows only one accordion open at a time
 */
export function SessionAccordionGroup({
  children,
  allowMultiple = true,
  defaultAllExpanded = false,
}) {
  const [expandedIds, setExpandedIds] = useState(
    defaultAllExpanded ? 'all' : new Set()
  );

  // This is a simple wrapper for now - can be enhanced with context
  // to coordinate accordion states if needed
  return (
    <div className="space-y-4">
      {children}
    </div>
  );
}
